import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Trash2,
  Check,
  Download,
  Pencil,
  AlertCircle,
} from "lucide-react";
import { useDossierStore } from "../../store/dossierStore";
import { createProduit, type TypeProduit } from "../../lib/produits";
import { normalizeUnite } from "../../lib/unites";
import { formatMontantFCFA } from "@/lib/formatMontant";

const STEPS = [
  { id: 1, label: "Choisir le fichier à importer" },
  { id: 2, label: "Comparer au modèle Woahda" },
  { id: 3, label: "Vérifier les données à importer" },
  { id: 4, label: "Import" },
] as const;

const MAPPING_OPTIONS = [
  { value: "nom", label: "Nom du produit" },
  { value: "prix_ht", label: "Prix HT" },
  { value: "taux_tva", label: "Taux de TVA" },
  { value: "reference", label: "Référence" },
  { value: "unite", label: "Unité" },
  { value: "type", label: "Type de produit" },
  { value: "", label: "— Ignorer" },
] as const;

const PAGE_SIZE_PREVIEW = 20;

/** Parse CSV générique : retourne en-têtes et lignes. */
function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 1) throw new Error("Le fichier est vide.");
  const sep = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0].split(sep).map((h) => h.trim().replace(/^["']|["']$/g, ""));
  if (headers.length === 0 || headers.every((h) => !h)) throw new Error("Aucune colonne détectée en première ligne.");
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ""));
    rows.push(cells);
  }
  return { headers, rows };
}

/** Génère le CSV template pour téléchargement. */
function getTemplateCSVBlob(): Blob {
  const headers = ["Nom du produit", "Prix HT", "Taux de TVA", "Référence", "Unité", "Type de produit"];
  const sample = ["Exemple prestation", "10000", "18", "REF-001", "Unité(s)", "prestation"];
  const line = [headers.join(";"), sample.join(";")].join("\n");
  return new Blob(["\uFEFF" + line], { type: "text/csv;charset=utf-8" });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " o";
  return (bytes / 1024).toFixed(2).replace(".", ",") + " kB";
}

export default function ProduitsImport() {
  const navigate = useNavigate();
  const { activeDossier } = useDossierStore();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | "success">(1);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [previewRows, setPreviewRows] = useState<Array<{ nom: string; prix_ht: number; taux_tva: number; reference?: string; unite?: string; type?: string }>>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  const [previewPage, setPreviewPage] = useState(1);

  const handleFileSelect = useCallback(
    (f: File | null) => {
      setFile(f);
      setFileError(null);
      if (!f) return;
      const ext = f.name.toLowerCase().split(".").pop();
      if (ext !== "csv") {
        setFileError(
          "Format non accepté. Utilisez un fichier CSV (.csv). Si vous avez un fichier Excel, enregistrez-le en CSV (Fichier → Enregistrer sous → CSV)."
        );
        return;
      }
      if (f.size > 2 * 1024 * 1024) {
        setFileError("Le fichier est trop volumineux (max. 2 Mo).");
        return;
      }
    },
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const f = e.dataTransfer.files?.[0];
      if (f) handleFileSelect(f);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), []);

  const handleStartImport = useCallback(async () => {
    if (!file || !activeDossier?.id) return;
    setFileError(null);
    try {
      const text = await file.text();
      const { headers: h, rows: r } = parseCSV(text);
      setHeaders(h);
      setRows(r);
      setMapping({});
      setStep(2);
    } catch (e) {
      setFileError(
        "Ce fichier n'a pas pu être lu. Vérifiez qu'il s'agit d'un CSV, que la première ligne contient les noms des colonnes et que le fichier est encodé en UTF-8."
      );
    }
  }, [file, activeDossier?.id]);

  const handleConfirmMapping = useCallback(() => {
    const nomKey = Object.entries(mapping).find(([, v]) => v === "nom")?.[0];
    const prixKey = Object.entries(mapping).find(([, v]) => v === "prix_ht")?.[0];
    if (!nomKey || !prixKey) return;
    const nomIdx = headers.indexOf(nomKey);
    const prixIdx = headers.indexOf(prixKey);
    const tvaKey = Object.entries(mapping).find(([, v]) => v === "taux_tva")?.[0];
    const tvaIdx = tvaKey !== undefined ? headers.indexOf(tvaKey) : -1;
    const refKey = Object.entries(mapping).find(([, v]) => v === "reference")?.[0];
    const refIdx = refKey !== undefined ? headers.indexOf(refKey) : -1;
    const uniteKey = Object.entries(mapping).find(([, v]) => v === "unite")?.[0];
    const uniteIdx = uniteKey !== undefined ? headers.indexOf(uniteKey) : -1;
    const typeKey = Object.entries(mapping).find(([, v]) => v === "type")?.[0];
    const typeIdx = typeKey !== undefined ? headers.indexOf(typeKey) : -1;

    const preview: typeof previewRows = [];
    for (const cells of rows) {
      const nom = (cells[nomIdx] ?? "").trim();
      if (!nom) continue;
      const prix = parseFloat(String(cells[prixIdx] ?? "0").replace(",", ".")) || 0;
      const tva = tvaIdx >= 0 ? parseFloat(String(cells[tvaIdx] ?? "18").replace(",", ".")) ?? 18 : 18;
      preview.push({
        nom,
        prix_ht: prix,
        taux_tva: tva,
        reference: refIdx >= 0 && cells[refIdx] ? cells[refIdx] : undefined,
        unite: uniteIdx >= 0 && cells[uniteIdx] ? cells[uniteIdx] : undefined,
        type: typeIdx >= 0 && cells[typeIdx] ? cells[typeIdx] : undefined,
      });
    }
    setPreviewRows(preview);
    setPreviewPage(1);
    setStep(3);
  }, [headers, mapping, rows]);

  const handleFinalizeImport = useCallback(async () => {
    if (!activeDossier?.id) return;
    setImporting(true);
    setImportResult(null);
    let success = 0;
    let failed = 0;
    for (const row of previewRows) {
      try {
        await createProduit(activeDossier.id, {
          nom: row.nom,
          reference: row.reference ?? null,
          prix_ht: row.prix_ht,
          taux_tva: row.taux_tva,
          unite: row.unite ? normalizeUnite(row.unite) || "Unité(s)" : "Unité(s)",
          type_produit: ["prestation", "marchandises", "compte_specifique"].includes(row.type ?? "")
            ? (row.type as TypeProduit)
            : "prestation",
        });
        success++;
      } catch {
        failed++;
      }
    }
    setImportResult({ success, failed });
    setImporting(false);
    setStep("success");
  }, [activeDossier?.id, previewRows]);

  if (!activeDossier) {
    return (
      <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px" }}>
        <div style={{ padding: "24px", background: "#FAF6F1", borderRadius: "12px", color: "#9A8878" }}>
          Sélectionnez un dossier.
        </div>
        <button
          type="button"
          onClick={() => navigate("/gestion/produits")}
          style={{ marginTop: 16, padding: "10px 20px", borderRadius: 10, border: "1px solid #EAE0D5", background: "white", cursor: "pointer", fontFamily: "'Sora', sans-serif" }}
        >
          ← Retour
        </button>
      </div>
    );
  }

  const canGoStep2 = file && !fileError && file.name.toLowerCase().endsWith(".csv");
  const canGoStep3 = headers.length > 0 && Object.values(mapping).filter((v) => v === "nom").length === 1 && Object.values(mapping).filter((v) => v === "prix_ht").length === 1;

  const totalPreviewPages = Math.max(1, Math.ceil(previewRows.length / PAGE_SIZE_PREVIEW));
  const previewPaginated = previewRows.slice((previewPage - 1) * PAGE_SIZE_PREVIEW, previewPage * PAGE_SIZE_PREVIEW);

  return (
    <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => (step === 1 ? navigate("/gestion/produits") : step === 2 ? setStep(1) : step === 3 ? setStep(2) : undefined)}
          style={{ padding: 8, border: "none", background: "transparent", cursor: "pointer", color: "#5A4A3A", borderRadius: 8 }}
          aria-label="Retour"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800, color: "#1A0A00" }}>Import de produits</h1>
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32, flexWrap: "wrap" }}>
        {STEPS.map((s, i) => {
          const done = step === "success" || (typeof step === "number" && step > s.id);
          const active = step === s.id;
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 10,
                  background: active ? "rgba(22,163,74,0.1)" : done ? "rgba(22,163,74,0.08)" : "transparent",
                  color: active || done ? "#16A34A" : "#9A8878",
                  fontWeight: active ? 700 : 600,
                  fontSize: "0.85rem",
                }}
              >
                {done ? <Check size={18} strokeWidth={2.5} /> : <span style={{ width: 20, textAlign: "center" }}>{s.id}</span>}
                {s.label}
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ width: 24, height: 2, background: done ? "#16A34A" : "#EAE0D5", opacity: done ? 1 : 0.5 }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Étape 1 */}
      {step === 1 && (
        <>
          <div
            style={{
              padding: "14px 18px",
              borderRadius: 12,
              border: "1px solid #3B82F6",
              background: "rgba(59,130,246,0.08)",
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: "0.9rem", color: "#1E40AF", fontWeight: 500 }}>
              Utilisez le modèle Woahda pour gagner du temps et éviter les erreurs d'import.
            </p>
            <a
              href={URL.createObjectURL(getTemplateCSVBlob())}
              download="modele_import_produits_woahda.csv"
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                background: "#16A34A",
                color: "white",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Download size={18} /> Télécharger
            </a>
          </div>

          <div
            style={{
              marginBottom: 16,
              padding: "14px 18px",
              borderRadius: 12,
              border: "1px solid #E5E7EB",
              background: "#F9FAFB",
              fontSize: "0.85rem",
              color: "#374151",
            }}
          >
            <strong style={{ color: "#1A0A00" }}>Format accepté pour ne pas être rejeté :</strong>
            <ul style={{ margin: "8px 0 0 20px", padding: 0 }}>
              <li>Fichier <strong>CSV</strong> (extension .csv), encodage UTF-8</li>
              <li>Première ligne = noms des colonnes (ex. Nom du produit, Prix HT, Taux de TVA)</li>
              <li>Colonnes obligatoires : <strong>Nom du produit</strong>, <strong>Prix HT</strong></li>
              <li>Optionnel : Taux de TVA, Référence, Unité, Type de produit</li>
              <li>Taille max. 2 Mo</li>
            </ul>
          </div>

          {fileError && (
            <div
              role="alert"
              style={{
                marginBottom: 24,
                padding: "16px 20px",
                borderRadius: 12,
                border: "1px solid #FCA5A5",
                background: "#FEF2F2",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <AlertCircle size={22} color="#DC2626" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#B91C1C", fontSize: "0.95rem" }}>Fichier non adapté</p>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#991B1B", lineHeight: 1.5 }}>{fileError}</p>
              </div>
            </div>
          )}

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: "2px dashed #D1D5DB",
              borderRadius: 16,
              padding: "48px 24px",
              textAlign: "center",
              background: "#FAFAFA",
              marginBottom: 24,
            }}
          >
            <FileSpreadsheet size={48} color="#9CA3AF" style={{ marginBottom: 16 }} />
            <p style={{ margin: "0 0 20px", fontSize: "1rem", fontWeight: 600, color: "#374151" }}>
              Glisser-déposer le fichier ici
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <label
                style={{
                  padding: "12px 24px",
                  borderRadius: 10,
                  border: "none",
                  background: "#16A34A",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Upload size={18} /> Ajouter
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          </div>

          {file && !fileError && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ margin: "0 0 8px", fontSize: "0.85rem", fontWeight: 600, color: "#6B7280" }}>Fichier ajouté</p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  background: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                }}
              >
                <span style={{ fontSize: "0.9rem", fontWeight: 500, color: "#1A0A00" }}>{file.name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: "0.85rem", color: "#6B7280" }}>{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => handleFileSelect(null)}
                    style={{ padding: 6, border: "none", background: "transparent", cursor: "pointer", color: "#DC2626" }}
                    aria-label="Supprimer le fichier"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={handleStartImport}
              disabled={!canGoStep2}
              style={{
                padding: "14px 32px",
                borderRadius: 12,
                border: "none",
                background: canGoStep2 ? "#16A34A" : "#9CA3AF",
                color: "white",
                fontWeight: 700,
                fontSize: "1rem",
                cursor: canGoStep2 ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Commencer l'import
            </button>
          </div>
        </>
      )}

      {/* Étape 2 */}
      {step === 2 && (
        <>
          <div style={{ marginBottom: 24 }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.75rem", fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>
              Colonne identifiée
            </p>
            <p style={{ margin: "0 0 16px", fontSize: "0.75rem", fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>
              Équivalent Woahda
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {headers.map((h) => (
                <div key={h} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ flex: 1, padding: "12px 14px", background: "#F9FAFB", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: "0.9rem", color: "#1A0A00" }}>
                    {h || "(sans nom)"}
                  </div>
                  <span style={{ color: "#9CA3AF" }}>→</span>
                  <select
                    value={mapping[h] ?? ""}
                    onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: "1px solid #E5E7EB",
                      background: "white",
                      fontSize: "0.9rem",
                      fontFamily: "'Sora', sans-serif",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">Sélectionner une correspondance</option>
                    {MAPPING_OPTIONS.map((opt) => (
                      <option key={opt.value || "ignore"} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleConfirmMapping}
              disabled={!canGoStep3}
              style={{
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                background: canGoStep3 ? "#16A34A" : "#9CA3AF",
                color: "white",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: canGoStep3 ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Confirmer les associations
            </button>
          </div>
        </>
      )}

      {/* Étape 3 */}
      {step === 3 && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => setStep(2)}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "1px solid #16A34A",
                background: "white",
                color: "#16A34A",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Pencil size={18} /> Modifier les associations
            </button>
            <button
              type="button"
              onClick={handleFinalizeImport}
              disabled={importing || previewRows.length === 0}
              style={{
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                background: importing || previewRows.length === 0 ? "#9CA3AF" : "#16A34A",
                color: "white",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: importing || previewRows.length === 0 ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {importing ? "Import en cours…" : "Finaliser l'import"}
            </button>
          </div>
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", background: "white" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "#6B7280", width: 60 }}>#</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "#6B7280" }}>Dénomination du produit</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: "#6B7280" }}>Prix HT</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700, color: "#6B7280" }}>Taux de TVA</th>
                </tr>
              </thead>
              <tbody>
                {previewPaginated.map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "12px 16px", color: "#6B7280" }}>{(previewPage - 1) * PAGE_SIZE_PREVIEW + i + 1}</td>
                    <td style={{ padding: "12px 16px", fontWeight: 500, color: "#1A0A00" }}>{row.nom}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: "#1A0A00" }}>
                      {formatMontantFCFA(row.prix_ht)}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center", color: "#1A0A00" }}>{row.taux_tva} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderTop: "1px solid #E5E7EB",
                background: "#F9FAFB",
                fontSize: "0.85rem",
                color: "#6B7280",
              }}
            >
              <span>{PAGE_SIZE_PREVIEW} éléments par page</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  {(previewPage - 1) * PAGE_SIZE_PREVIEW + 1}-{Math.min(previewPage * PAGE_SIZE_PREVIEW, previewRows.length)} sur {previewRows.length}
                </span>
                <button
                  type="button"
                  onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                  disabled={previewPage <= 1}
                  style={{ padding: "6px 10px", border: "1px solid #E5E7EB", borderRadius: 6, background: "white", cursor: previewPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  ‹
                </button>
                <span style={{ padding: "6px 10px", fontWeight: 600, color: "#16A34A" }}>{previewPage}</span>
                <button
                  type="button"
                  onClick={() => setPreviewPage((p) => Math.min(totalPreviewPages, p + 1))}
                  disabled={previewPage >= totalPreviewPages}
                  style={{ padding: "6px 10px", border: "1px solid #E5E7EB", borderRadius: 6, background: "white", cursor: previewPage >= totalPreviewPages ? "not-allowed" : "pointer" }}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Succès */}
      {step === "success" && (
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(22,163,74,0.2)",
              color: "#16A34A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Check size={40} strokeWidth={2.5} />
          </div>
          <h2 style={{ margin: "0 0 12px", fontSize: "1.5rem", fontWeight: 700, color: "#1A0A00" }}>
            Les produits ont été importés !
          </h2>
          <p style={{ margin: "0 0 8px", fontSize: "1rem", color: "#5A4A3A", lineHeight: 1.5 }}>
            L'import des produits est désormais terminé.
          </p>
          {importResult && (
            <p style={{ margin: "0 0 24px", fontSize: "0.9rem", color: "#6B7280" }}>
              {importResult.success} produit(s) créé(s). {importResult.failed > 0 ? `${importResult.failed} erreur(s).` : ""}
            </p>
          )}
          <p style={{ margin: "0 0 32px", fontSize: "0.95rem", color: "#5A4A3A" }}>
            Vous pouvez retrouver les produits directement depuis la page de gestion des produits.
          </p>
          <button
            type="button"
            onClick={() => navigate("/gestion/produits")}
            style={{
              padding: "14px 28px",
              borderRadius: 12,
              border: "none",
              background: "#16A34A",
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: "pointer",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            Se rendre sur la page des produits
          </button>
        </div>
      )}
    </div>
  );
}
